import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useHistory, useLocation, useParams } from "react-router-dom";
import AdminHeader from "../../../header/admin_header";
import AdminSidebar from "../../../sidebar/admin_sidebar";
import { AssessmentService } from "../../../../_services/admin/assessmentService";
import KpiComposer from "./KpiComposer";
import { flattenForSubmit, flattenFromServerTree } from "./kpiTree";
import "../../../Sector_Question_Manage/control.css";

/**
 * Create / edit a RIU template. Single component handles both modes — the
 * presence of `:id` in the route puts us in edit mode (drafts only; the
 * Templates list only links here when status_key === "DRAFT").
 *
 * KPI composition lives in `<KpiComposer />`; this page owns the shell
 * form (title, description, industry cascade, status) and the submit.
 */
const CreateAssessment = () => {
  const location = useLocation();
  const history = useHistory();
  const { id: assessmentId } = useParams();
  const isEdit = Boolean(assessmentId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [industry, setIndustry] = useState("");
  const [subIndustry, setSubIndustry] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [kpis, setKpis] = useState([]);

  const [industries, setIndustries] = useState([]);
  const [subIndustriesByIndustry, setSubIndustriesByIndustry] = useState({});
  const [kpiCatalog, setKpiCatalog] = useState({});

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [iRes, kRes] = await Promise.all([
        AssessmentService.listIndustries(),
        AssessmentService.listKpis(),
      ]);

      const industryList = iRes?.data?.data ?? iRes?.data ?? [];
      if (Array.isArray(industryList)) {
        const grouped = {};
        const seen = industryList.map((row) => {
          grouped[row.industry_type_id] = (row.items ?? []).map((sub) => ({
            id: sub.id,
            name: sub.title,
          }));
          return { id: row.industry_type_id, name: row.industry_type_name };
        });
        setIndustries(seen);
        setSubIndustriesByIndustry(grouped);
      }

      const catalogList = kRes?.data?.data ?? [];
      if (Array.isArray(catalogList)) {
        const map = {};
        catalogList.forEach((k) => {
          map[k.id] = k.title;
        });
        setKpiCatalog(map);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      const res = await AssessmentService.getAssessment(assessmentId);
      if (!res?.isSuccess) return;
      const a = res?.data?.data;
      if (!a) return;
      setTitle(a.title || "");
      setDescription(a.description || "");
      setIndustry(String(a.industry ?? ""));
      setSubIndustry(String(a.sub_industry ?? ""));
      setStatus(a.status_key || "DRAFT");
      setKpis(flattenFromServerTree(a.kpis || [], kpiCatalog));
    })();
  }, [assessmentId, isEdit, kpiCatalog]);

  const subIndustries = industry
    ? subIndustriesByIndustry[industry] || []
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !industry || !subIndustry) return;
    setSubmitting(true);
    const payload = {
      title,
      description: description || null,
      industry: Number(industry),
      sub_industry: Number(subIndustry),
      status,
      kpis: flattenForSubmit(kpis),
    };
    const res = isEdit
      ? await AssessmentService.updateAssessment(assessmentId, payload)
      : await AssessmentService.createAssessment(payload);
    setSubmitting(false);
    if (res?.isSuccess) {
      const newId = res?.data?.data?.id || assessmentId;
      history.push(
        newId ? `/supplierassessment/view/${newId}` : "/supplierassessment"
      );
    }
  };

  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />
      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="color_div_on framwork_2">
            <div className="d-flex align-items-center justify-content-between border-bottom mb-3 pb-2">
              <div className="heading">
                <h4 className="m-0">
                  <strong>
                    {isEdit
                      ? "Edit Assessment Template"
                      : "Create Assessment Template"}
                  </strong>
                </h4>
              </div>
              <button
                type="button"
                className="link_bal_next_cancel"
                onClick={() => history.goBack()}
              >
                Back
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <Row>
                <Col md={12}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-heading text-primary me-2"></i>{" "}
                      Title*
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. BRSR Scope-3 baseline"
                      required
                    />
                  </div>
                </Col>

                <Col md={12}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-align-left text-primary me-2"></i>{" "}
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                </Col>

                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-industry text-primary me-2"></i>{" "}
                      Industry*
                    </label>
                    <select
                      className="form-select"
                      required
                      value={industry}
                      onChange={(e) => {
                        setIndustry(e.target.value);
                        setSubIndustry("");
                      }}
                      disabled={isEdit}
                      title={
                        isEdit
                          ? "Industry cannot be changed after creation"
                          : undefined
                      }
                      style={isEdit ? { cursor: "not-allowed" } : undefined}
                    >
                      <option value="">Select Industry</option>
                      {industries.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-layer-group text-primary me-2"></i>{" "}
                      Sub-industry*
                    </label>
                    <select
                      className="form-select"
                      required
                      disabled={isEdit || !industry}
                      title={
                        isEdit
                          ? "Sub-industry cannot be changed after creation"
                          : undefined
                      }
                      value={subIndustry}
                      onChange={(e) => setSubIndustry(e.target.value)}
                      style={isEdit ? { cursor: "not-allowed" } : undefined}
                    >
                      <option value="">Select Sub-industry</option>
                      {subIndustries.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>

                <Col md={12}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-flag text-primary me-2"></i> Status
                    </label>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 200 }}
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="DRAFT">DRAFT</option>
                      <option value="PUBLISHED">PUBLISHED</option>
                    </select>
                  </div>
                </Col>

                <Col md={12}>
                  <KpiComposer
                    kpis={kpis}
                    setKpis={setKpis}
                    kpiCatalog={kpiCatalog}
                  />
                </Col>
              </Row>

              <div className="text-end border-top pt-3 mt-2">
                <button
                  type="button"
                  className="link_bal_next_cancel"
                  onClick={() => history.goBack()}
                >
                  Cancel
                </button>
                &nbsp;
                <button
                  type="submit"
                  className="link_bal_next"
                  disabled={submitting}
                >
                  {submitting
                    ? isEdit
                      ? "Saving…"
                      : "Creating…"
                    : isEdit
                      ? "Save Changes"
                      : "Create Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAssessment;
